import { Component, ElementRef, afterNextRender, inject, input, output, signal, viewChild } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { DropdownSearchService } from "./dropdown-search.service";

// Popup search box shared by the dropdown wrappers; rendered in their Kendo header template.
@Component({
    selector: "wk-dropdown-search-box",
    templateUrl: "./dropdown-search-box.component.html",
    host: {
        class: "wk-dropdown-search k-list-filter",
        "(focusin)": "focused.emit()",
        "(focusout)": "onFocusOut($event)",
    },
    imports: [TranslatePipe],
})
export class DropdownSearchBoxComponent {
    // Keys the host Kendo component must still receive to drive its option list.
    private static readonly NAVIGATION_KEYS = new Set(["Enter", "ArrowUp", "ArrowDown", "PageUp", "PageDown"]);

    private readonly search = inject(DropdownSearchService);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly inputRef = viewChild.required<ElementRef<HTMLInputElement>>("searchInput");
    private readonly clearRef = viewChild.required<ElementRef<HTMLButtonElement>>("clearButton");

    readonly clearLabel = input("");

    readonly navigate = output<KeyboardEvent>();
    // Tab order is owned by the wrapper's popup layout; the popup may sit outside the wrapper, so it is reported.
    readonly tab = output<KeyboardEvent>();
    readonly focused = output<void>();
    // Focus left the search box (input and clear button) for somewhere else.
    readonly blurred = output<void>();

    protected readonly text = signal("");

    constructor() {
        // The box only exists while the popup is open: typing should land here.
        afterNextRender(() => this.focus());
    }

    focus(): void {
        this.inputRef().nativeElement.focus({ preventScroll: true });
    }

    stops(): HTMLElement[] {
        return [this.inputRef().nativeElement, this.clearRef().nativeElement];
    }

    protected onInput(value: string): void {
        this.text.set(value);
        this.search.onFilterChange(value);
    }

    // Focus goes back to the input before the button disables itself, so it is never dropped to the body.
    protected clear(): void {
        this.text.set("");
        this.search.reset();
        this.focus();
    }

    // Escape bubbles so the host can close the popup; other keys stay out of the host's type-ahead.
    protected onInputKeydown(event: KeyboardEvent): void {
        if (event.key === "Escape") return;

        event.stopPropagation();
        if (event.key === "Tab") {
            this.tab.emit(event);
        } else if (DropdownSearchBoxComponent.NAVIGATION_KEYS.has(event.key)) {
            event.preventDefault();
            this.navigate.emit(event);
        }
    }

    // Enter / Space activate the button natively and must not also select the highlighted option.
    protected onButtonKeydown(event: KeyboardEvent): void {
        if (event.key === "Escape") return;

        event.stopPropagation();
        if (event.key === "Tab") this.tab.emit(event);
    }

    protected onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget;
        if (next instanceof Node && this.host.nativeElement.contains(next)) return;

        this.blurred.emit();
    }
}
